import { DragDropContext, DraggableLocation, DropResult } from '@hello-pangea/dnd';
import { CheckCircleOutlined, InfoOutlined, WarningAmberRounded } from '@mui/icons-material';
import { Box, Divider, SxProps, Theme, Typography } from '@mui/material';

import { useLanguage } from '../../hooks';
import {
  SignatureOrderType,
  SignatureOrderUser,
  SignerData,
  SignersStateGroups as SignersState,
} from '../../types/taskForm';
import { SignatureOrderConfig } from './SignatureOrderConfig';

interface SignatureOrderProps {
  signersState: SignersState;
  onSignersOrderChange: (
    sequentialOrder: SignatureOrderUser[][],
    simultaneousOrder: SignatureOrderUser[][],
  ) => void;
  onSignerDelete: (signerId: string | number) => void;
  disabled: boolean;
  isLocationActive: boolean;
  authorMustSign: boolean;
  isQuorumEnabled?: boolean;
  quorumBlocked?: boolean;
  minimumSignatures?: (number | null)[];
  onMinimumSignaturesChange?: (minimumSignatures: (number | null)[]) => void;
}

const SignatureOrderStateType = {
  ...SignatureOrderType,
  INCOMPLETE: 'incomplete',
};

const customStyles: Record<string, SxProps<Theme>> = {
  root: {
    display: 'flex',
    padding: '25px 0px',
    gap: '20px',
  },
  divider: {
    height: 'unset',
  },
  infoIcon: {
    color: (theme) => theme.palette.primary.main,
    fontSize: '16px',
  },
  disabledInfoIcon: {
    color: '#6E7178',
    fontSize: '16px',
    opacity: 0.5,
  },
  warningIcon: {
    color: '#D2B306',
    fontSize: '16px',
  },
  successIcon: {
    color: '#66D83C',
    fontSize: '16px',
  },
};

export function SignatureOrder({
  signersState,
  onSignersOrderChange,
  onSignerDelete,
  disabled,
  isLocationActive,
  authorMustSign,
  isQuorumEnabled = false,
  quorumBlocked = false,
  minimumSignatures = [],
  onMinimumSignaturesChange,
}: SignatureOrderProps) {
  const lang = useLanguage();

  const {
    simultaneousOrder,
    sequentialOrder,
    selectedUsers,
    externalUsersEmails,
    selectedQuestions,
  } = signersState;
  const usersData = {
    ...selectedUsers.reduce((acc, user) => {
      acc[user.id.toString()] = {
        id: user.id.toString(),
        name: user.name?.trim() || user.email,
        is_external: false,
        is_location: false,
        question_id: null,
      };
      return acc;
    }, {} as Record<string, SignerData>),
    ...externalUsersEmails.reduce((acc, email) => {
      acc[email] = {
        id: email,
        name: email,
        is_external: true,
        is_location: false,
        question_id: null,
      };
      return acc;
    }, {} as Record<string, SignerData>),
    ...selectedQuestions.reduce((acc, question) => {
      acc[`q_${question.id}`] = {
        id: `q_${question.id}`,
        name: question.title,
        is_external: false,
        is_location: false,
        question_id: question.id,
      };
      return acc;
    }, {} as Record<string, SignerData>),
    ...(isLocationActive
      ? {
          ['location']: {
            id: 'location',
            name: lang.formSignatureBuilder.locationUser,
            is_external: false,
            is_location: true,
            question_id: null,
          },
        }
      : ({} as Record<string, SignerData>)),
    ...((authorMustSign
      ? {
          author: {
            id: 'author',
            is_external: false,
            is_location: false,
            name: lang.formSignatureBuilder.author,
            question_id: null,
          },
        }
      : {}) as Record<string, SignerData>),
  };

  const isIncomplete = simultaneousOrder.length > 0 && sequentialOrder.length > 0;

  const isSequential =
    sequentialOrder.length === Object.keys(usersData).length && Object.keys(usersData).length > 0;
  const orderType = isSequential
    ? SignatureOrderStateType.SEQUENTIAL
    : SignatureOrderStateType.SIMULTANEOUS;
  const configState = isIncomplete ? SignatureOrderStateType.INCOMPLETE : orderType;

  const generateGroupId = (signers: SignatureOrderUser[]) => {
    return signers.map((signer) => signer.id.toString()).join('-');
  };

  const handleCombineSigners = (
    source: DraggableLocation,
    combine: { droppableId: string; draggableId: string },
    sequentialOrderCopy: SignatureOrderUser[][],
    simultaneousOrderCopy: SignatureOrderUser[][],
  ): void => {
    if (combine.droppableId === SignatureOrderStateType.SIMULTANEOUS) {
      return;
    }

    let movedItems: SignatureOrderUser[] = [];

    if (source.droppableId === SignatureOrderStateType.SIMULTANEOUS) {
      [movedItems] = simultaneousOrderCopy.splice(source.index, 1);
    } else {
      [movedItems] = sequentialOrderCopy.splice(source.index, 1);
    }

    const targetIndex = sequentialOrderCopy.findIndex(
      (signers) => generateGroupId(signers) === combine.draggableId,
    );

    if (targetIndex !== -1) {
      sequentialOrderCopy[targetIndex].push(...movedItems);
    }

    onSignersOrderChange(sequentialOrderCopy, simultaneousOrderCopy);
  };

  const handleMoveSigners = (
    source: DraggableLocation,
    destination: DraggableLocation,
    sequentialOrderCopy: SignatureOrderUser[][],
    simultaneousOrderCopy: SignatureOrderUser[][],
  ): void => {
    if (source.droppableId === destination.droppableId) {
      const list =
        source.droppableId === SignatureOrderStateType.SIMULTANEOUS
          ? simultaneousOrderCopy
          : sequentialOrderCopy;

      const [movedItem] = list.splice(source.index, 1);
      if (movedItem) {
        list.splice(destination.index, 0, movedItem);
      }
    } else {
      const destList =
        destination.droppableId === SignatureOrderStateType.SIMULTANEOUS
          ? simultaneousOrderCopy
          : sequentialOrderCopy;

      const sourceList =
        source.droppableId === SignatureOrderStateType.SIMULTANEOUS
          ? simultaneousOrderCopy
          : sequentialOrderCopy;

      const [movedItems] = sourceList.splice(source.index, 1);
      movedItems.forEach((item) => {
        destList.splice(destination.index, 0, [item]);
      });
    }
    onSignersOrderChange(sequentialOrderCopy, simultaneousOrderCopy);
  };

  const handleDragEnd = (result: DropResult) => {
    if (disabled) return;

    const { source, destination, combine } = result;
    const sequentialOrderCopy = [...sequentialOrder];
    const simultaneousOrderCopy = [...simultaneousOrder];

    if (combine) {
      handleCombineSigners(source, combine, sequentialOrderCopy, simultaneousOrderCopy);
    } else if (destination) {
      handleMoveSigners(source, destination, sequentialOrderCopy, simultaneousOrderCopy);
    }
  };

  const handleUnGroup = (signerId: string | number, index: number) => {
    let removedSigner: SignatureOrderUser | undefined;
    const newSequentialOrder = sequentialOrder.map((signers, signersIndex) => {
      if (signersIndex === index) {
        removedSigner = signers.find((signer) => String(signer.id) === String(signerId));
        return signers.filter((signer) => String(signer.id) !== String(signerId));
      }
      return signers;
    });

    if (removedSigner) {
      newSequentialOrder.splice(index + 1, 0, [removedSigner]);
    }
    onSignersOrderChange(newSequentialOrder, simultaneousOrder);
  };

  const sequentialInfo = {
    [SignatureOrderStateType.SIMULTANEOUS]: {
      icon: <InfoOutlined sx={disabled ? customStyles.disabledInfoIcon : customStyles.infoIcon} />,
      text: lang.formSignatureBuilder.sequentialSignInfo,
    },
    [SignatureOrderStateType.SEQUENTIAL]: {
      icon: <CheckCircleOutlined sx={customStyles.successIcon} />,
      text: lang.formSignatureBuilder.sequentialSignSuccess,
    },
    [SignatureOrderStateType.INCOMPLETE]: {
      icon: <WarningAmberRounded sx={customStyles.warningIcon} />,
      text: (
        <>
          {`${lang.formSignatureBuilder.sequentialWarningInfo.split('-')[0]} -`}
          <Typography
            component='span'
            fontWeight='bold'
          >
            {lang.formSignatureBuilder.sequentialWarningInfo
              .split('-')[1]
              .replace('{value}', `${sequentialOrder.length}/${Object.keys(usersData).length}`)}
          </Typography>
        </>
      ),
    },
  };
  return (
    <Box sx={customStyles.root}>
      <DragDropContext onDragEnd={handleDragEnd}>
        <SignatureOrderConfig
          type={SignatureOrderType.SIMULTANEOUS}
          isDragAndDropDisabled={disabled}
          isIncomplete={isIncomplete}
          order={simultaneousOrder}
          usersData={usersData}
          onSignerDelete={onSignerDelete}
          isQuorumEnabled={isQuorumEnabled}
          quorumBlocked={quorumBlocked}
          minimumSignatures={minimumSignatures}
          onMinimumSignaturesChange={onMinimumSignaturesChange}
        />
        <Divider
          sx={customStyles.divider}
          orientation='vertical'
        />
        <SignatureOrderConfig
          type={SignatureOrderType.SEQUENTIAL}
          isDragAndDropDisabled={disabled}
          order={sequentialOrder}
          usersData={usersData}
          onSignerDelete={onSignerDelete}
          isCombineEnabled
          handleUnGroup={handleUnGroup}
          sequentialInfo={sequentialInfo}
          configState={configState}
          isQuorumEnabled={isQuorumEnabled}
          quorumBlocked={quorumBlocked}
          minimumSignatures={minimumSignatures}
          onMinimumSignaturesChange={onMinimumSignaturesChange}
        />
      </DragDropContext>
    </Box>
  );
}
